/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import './style/viewer.css';
// Pins the document so the map does not scroll with the window. Only
// this entry: the article and per-point pages are prose and must scroll.
import './style/map-page.css';

/*
 * Map Viewer entry point.
 *
 * It starts the Map Viewer, and re-exports the modules so a page that is not
 * the Map Viewer — or a console — can reach them.
 *
 * Starting it is the part that is easy to leave out. The scripts this
 * replaced each registered themselves on `DOMContentLoaded`, so loading
 * one was the same as running it. A module only runs when something calls
 * it, and a bundle whose entry merely re-exports is a no-op that every
 * unit test still passes, because the tests call `init` themselves.
 */

import { initAll } from './pages/index.js';

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initAll();
    });
} else {
    initAll();
}

export { a11y } from './lib/a11y.js';
export { browser, getVendorCSS } from './lib/browser.js';
export { isDarkColor, parseColor } from './lib/color.js';
export {
    MAP_STATE_COOKIE,
    NODES_VISITED_LIST_COOKIE,
} from './lib/constants.js';
export {
    initMenu,
    keepAspectRatio,
    layout,
    scrollToY,
} from './lib/dom.js';
export { animateHeight } from './lib/animate.js';
export { getConfig, setConfig } from './lib/config.js';
export { contentPreprocessors } from './lib/content-preprocessors.js';
export { cookie, cookieConsent } from './lib/cookie.js';
export { openDialog, videoLightbox } from './lib/dialog.js';
export { emit, off, offTarget, on, onTarget } from './lib/events.js';
export { Glossary } from './lib/glossary.js';
export { language, setLanguage, t } from './lib/i18n.js';
export { Keyboard } from './lib/map-keyboard.js';
export { Lightbox } from './lib/lightbox.js';
export { KontextMap } from './lib/map.js';
export { setSocial } from './lib/map-popup.js';
export { containsMath, detectMath, renderMath } from './lib/math.js';
export {
    attachSubtitleTrack,
    isMediaURL,
    linksToAudioPlayer,
    linksToMediaPlayer,
    linksToVideoPlayer,
    parseMediaURL,
    rewriteAudioElement,
    rewriteImageElement,
    rewriteImageURL,
    rewriteMediaElements,
    rewriteMediaURL,
    rewriteMediaUrls,
    rewriteVideoElement,
    stopAudioVideo,
    videoToVideoPlayer,
} from './lib/media.js';
export {
    currentHost,
    externalLinkInNewTab,
    getMapSlugFromLocation,
    getRelativeLink,
    injectParams,
    internalLinkTarget,
    isCurrentMapURL,
    isEmbedded,
    isExternalLink,
    isLocalhost,
    rewriteInternalUrlsForApp,
    stripPathForApp,
    urlExists,
} from './lib/links.js';
export { PopupPlugin } from './lib/popup-plugin.js';
export { Social } from './lib/social.js';
export { Tabs, TABS_HIDE_DURATION, TABS_SHOW_DURATION } from './lib/tabs.js';
export { formatTimes } from './lib/time.js';
export { stripHTML, translate } from './lib/text.js';
export { createAccordion } from './lib/accordion.js';
export { createCombobox } from './lib/combobox.js';
export { createTooltip } from './lib/tooltip.js';
export {
    applyStyleSet,
    limit,
    numStr,
    languageDisplayName,
    smartStripLineBreaks,
} from './lib/util.js';

export { initEditorial } from './lib/editorial.js';
export {
    getGlossary,
    getPopupInstances,
    getSocialPanel,
    setGlossary,
    setPopupInstances,
    setSocialPanel,
} from './lib/instances.js';

export { setupPage } from './pages/page-setup.js';
export * as pages from './pages/index.js';
